/**
 * Sunbiz Dashboard — control panel for the Sunbiz → AgenteLead Pro pipeline.
 *
 * 4 tabs:
 *   1. Dashboard  → KPI cards + 30-day series + manual Run Now
 *   2. Leads      → table of captured rows with filters + reprocess action
 *   3. Config     → cron schedule + toggles per stage + extra cities + Resend campaign id
 *   4. Logs       → past pipeline_runs (status, counts, error_message)
 *
 * Access: master / super_admin / master_demo (master_demo is read-only —
 * "Run Now" + Save buttons return 403 from the backend).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Play, RefreshCw, Save, AlertCircle, CheckCircle2, Loader2,
  Search, Building2, MapPin, Database, Mail, Sparkles, Clock,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import ClaireHelpButton from "../components/ClaireHelpButton";
import LeadDetailDrawer from "../components/LeadDetailDrawer";
import EmailModal from "../components/EmailModal";
import { US_STATES, US_CITIES_BY_STATE } from "../data/us_locations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  filtered:  { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D" },
  enriched:  { bg: "#DBEAFE", fg: "#1E40AF", border: "#93C5FD" },
  injected:  { bg: "#D1FAE5", fg: "#065F46", border: "#6EE7B7" },
  failed:    { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5" },
  raw:       { bg: "#E5E7EB", fg: "#374151", border: "#9CA3AF" },
};

function Kpi({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="al-card p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#6B7280]">{label}</div>
      <div className="mt-2 font-display text-[28px] font-black tracking-tight" style={{ color: accent || "#111827" }}>{value}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.raw;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10.5px] font-bold uppercase tracking-wide"
      style={{ backgroundColor: c.bg, color: c.fg, borderColor: c.border }}>
      {status}
    </span>
  );
}

export default function Sunbiz() {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [runs, setRuns] = useState([]);
  const [config, setConfig] = useState(null);
  const [running, setRunning] = useState(false);
  // Iter108 — lead detail drawer + email modal + state/city dropdown.
  const [filterState, setFilterState] = useState("FL");
  const [drawerLead, setDrawerLead] = useState(null);
  const [emailLead, setEmailLead] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [email, setEmail] = useState(null);
  const [templatePicker, setTemplatePicker] = useState(false);
  const [templates, setTemplates] = useState([]);
  // Iter109 — 1-click campaign state.
  const [selected, setSelected] = useState(new Set());
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [bodyTemplates, setBodyTemplates] = useState([]);
  const [subjectTemplates, setSubjectTemplates] = useState([]);
  const [pickedBody, setPickedBody] = useState("");
  const [pickedSubject, setPickedSubject] = useState("");
  const [campaignRunning, setCampaignRunning] = useState(false);
  const [campaignReport, setCampaignReport] = useState(null);

  const cityOptions = useMemo(
    () => US_CITIES_BY_STATE[filterState] || [],
    [filterState],
  );
  const [filters, setFilters] = useState({ status: "", city: "" });
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [s, d, l, r, c] = await Promise.all([
        axios.get(`${API}/sunbiz/stats`),
        axios.get(`${API}/sunbiz/daily?days=30`),
        axios.get(`${API}/sunbiz/leads`, { params: { limit: 200 } }),
        axios.get(`${API}/sunbiz/runs?limit=30`),
        axios.get(`${API}/sunbiz/config`),
      ]);
      setStats(s.data);
      setSeries(d.data.series || []);
      setLeads(l.data.items || []);
      setLeadsTotal(l.data.total || 0);
      setRuns(r.data.runs || []);
      setConfig(c.data.config);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar Sunbiz");
    }
  };
  useEffect(() => { loadAll(); }, []);

  const refreshLeadsFiltered = async () => {
    const { data } = await axios.get(`${API}/sunbiz/leads`, {
      params: {
        limit: 200,
        status: filters.status || undefined,
        city: filters.city || undefined,
        file_date: filters.file_date || undefined,
        captured_date: filters.captured_date || undefined,
      },
    });
    setLeads(data.items || []);
    setLeadsTotal(data.total || 0);
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/sunbiz/run`, {
        days_back: config?.days_back || 3,
        max_items: config?.max_items || 500,
        filing_types: [],  // Iter106 — let actor return ALL filing types
        skip_enrich: !(config?.enable_enrich ?? true),
        skip_inject: !(config?.enable_inject ?? true),
        skip_outreach: !(config?.enable_outreach ?? false),
      });
      const run = data.run || {};
      if (run.leads_captured === 0) {
        toast.warning(
          "Apify retornou 0 filings. Pode ser fim de semana/feriado. Tente aumentar 'Dias atrás' nas configurações.",
        );
      } else if (run.leads_filtered === 0) {
        toast.warning(
          `Capturados ${run.leads_captured}, mas nenhum em Orlando/Orange County hoje. Tente novamente amanhã.`,
        );
      } else {
        toast.success(
          `Pipeline ${run.status} · ${run.leads_captured} capturados → ${run.leads_filtered} Orlando → ${run.leads_injected} injetados`,
        );
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pipeline falhou");
    } finally {
      setRunning(false);
    }
  };

  const runDiagnose = async () => {
    try {
      const { data } = await axios.get(`${API}/sunbiz/diagnose?days_back=${config?.days_back || 3}`);
      const lines = [
        `total_captured: ${data.total_captured}`,
        `matched_orlando: ${data.matched_orlando}`,
        `top_cities: ${(data.top_cities || []).slice(0, 5).map(([c, n]) => `${c}(${n})`).join(", ")}`,
        `filing_types: ${Object.entries(data.filing_types || {}).map(([t, n]) => `${t}(${n})`).join(", ")}`,
      ];
      toast.info(lines.join("  ·  "), { duration: 10000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnose falhou");
    }
  };

  const reprocessLead = async (doc) => {
    try {
      await axios.post(`${API}/sunbiz/leads/${doc}/reprocess`);
      toast.success("Reprocessado");
      refreshLeadsFiltered();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  // Iter108 — Build a LeadDetailDrawer-compatible shape from a Sunbiz row.
  const sunbizToLead = (l) => ({
    id: l.id || l.document_number,
    name: l.company_name || "—",
    address: [l.principal_address, l.principal_city, "FL", l.principal_zip].filter(Boolean).join(", "),
    sector: l.industry || "Sunbiz",
    rating: null,
    review_count: null,
    has_website: !!l.website,
    website: l.website || "",
    phone: l.phone || "",
    email: l.email || "",
    linkedin: l.linkedin_url || "",
    types: ["Sunbiz", "new_business"],
    status: l.status === "injected" ? "open" : "operational",
    score: 70,
    badge: "MORNO",
    google_maps_url: l.principal_address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${l.company_name} ${l.principal_address}`)}`
      : "",
    sunbiz: {
      document_number: l.document_number,
      file_date: l.file_date,
      filing_type: l.filing_type,
      officer_name: l.officer_name,
      officer_title: l.officer_title,
      registered_agent: l.registered_agent,
      enrichment_source: l.enrichment_source,
      outreach_triggered: !!l.outreach_triggered,
      // Iter115 — used by ExternalLookups (Bizapedia, TruePeopleSearch).
      company_name: l.company_name,
      principal_city: l.principal_city,
      principal_state: "FL",
    },
  });

  const openLeadDetail = (l) => setDrawerLead(sunbizToLead(l));

  const handleSunbizEmail = async (l) => {
    if (!l.email) {
      toast.error("Sem e-mail enriquecido para esse lead — rode o enrich pipeline primeiro.");
      return;
    }
    const lead = sunbizToLead(l);
    setEmailLead(lead);
    setEmail(null);
    setEmailOpen(true);
    setEmailLoading(true);
    try {
      const { data } = await axios.post(`${API}/generate-email`, {
        lead_id: lead.id, name: lead.name, sector: lead.sector,
        address: lead.address, language: "pt", tone: "consultative",
        pain_points: ["new florida llc — first 90 days"],
      });
      setEmail({ ...data.email, language: data.language, tone: data.tone });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar e-mail");
      setEmailOpen(false);
    } finally {
      setEmailLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/library/templates?kind=email_body&language=pt`);
      setTemplates(data.items || []);
    } catch (e) { /* silent */ }
  };

  // Iter109 — open the 1-click Campaign modal (loads body + subject templates).
  const openCampaign = async () => {
    if (selected.size === 0) {
      toast.error("Selecione ao menos 1 lead antes.");
      return;
    }
    setCampaignOpen(true);
    setCampaignReport(null);
    try {
      const [b, s] = await Promise.all([
        axios.get(`${API}/library/templates?kind=email_body&language=pt`),
        axios.get(`${API}/library/templates?kind=email_subject&language=pt`),
      ]);
      const bs = b.data?.items || [];
      const ss = s.data?.items || [];
      setBodyTemplates(bs);
      setSubjectTemplates(ss);
      // Auto-suggest sunbiz-category templates first.
      const sb = bs.find((t) => (t.category || "").toLowerCase() === "sunbiz");
      const ss1 = ss.find((t) => (t.category || "").toLowerCase() === "sunbiz");
      setPickedBody(sb?.id || (bs[0] && bs[0].id) || "");
      setPickedSubject(ss1?.id || (ss[0] && ss[0].id) || "");
    } catch (e) {
      toast.error("Falha ao carregar templates.");
      setCampaignOpen(false);
    }
  };

  const runCampaign = async () => {
    if (!pickedBody) {
      toast.error("Escolha um template de corpo.");
      return;
    }
    setCampaignRunning(true);
    try {
      const { data } = await axios.post(`${API}/sunbiz/campaigns/run`, {
        lead_ids: Array.from(selected),
        body_template_id: pickedBody,
        subject_template_id: pickedSubject || null,
        skip_already_sent: true,
      });
      setCampaignReport(data);
      toast.success(
        `Campanha disparada · ${data.sent} enviados · ${data.skipped} pulados · ${data.failed} falharam`,
      );
      // Clear selection only if no failures so the user can retry failed ones.
      if (data.failed === 0) setSelected(new Set());
      refreshLeadsFiltered();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Campanha falhou.");
    } finally {
      setCampaignRunning(false);
    }
  };

  const toggleSelect = (doc) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(doc)) n.delete(doc); else n.add(doc);
      return n;
    });
  };

  const toggleSelectAll = () => {
    const visibleEligible = leads.filter((l) => l.email).map((l) => l.document_number);
    setSelected((prev) => {
      if (visibleEligible.every((d) => prev.has(d)) && visibleEligible.length > 0) {
        return new Set();
      }
      return new Set(visibleEligible);
    });
  };

  const applyTemplate = (tpl) => {
    if (!emailLead) return;
    const subjTpl = templates.find((t) => t.kind === "email_subject" && t.category === tpl.category);
    setEmail({
      subject: (subjTpl?.content || `Sobre {company_name}`).replace(/{company_name}/g, emailLead.name),
      body:    tpl.content
        .replace(/{first_name}/g,   (emailLead.sunbiz?.officer_name || emailLead.name).split(" ")[0])
        .replace(/{company_name}/g, emailLead.name)
        .replace(/{city}/g,         emailLead.sunbiz?.principal_city || "")
        .replace(/{sector}/g,       emailLead.sector || "")
        .replace(/{sender_name}/g,  "Equipe AgenteLead Pro"),
      language: tpl.language,
      tone: "consultative",
    });
    setTemplatePicker(false);
    toast.success(`Template aplicado: ${tpl.name}`);
  };

  const PRESETS = {
    "Orlando / Orange County": {
      target_cities: ["ORLANDO", "WINDERMERE", "WINTER PARK", "OCOEE", "APOPKA",
        "ALTAMONTE SPRINGS", "MAITLAND", "CASSELBERRY", "LONGWOOD",
        "WINTER GARDEN", "BELLE ISLE", "EATONVILLE"],
      target_zip_prefixes: ["328"],
    },
    "Tampa Bay": {
      target_cities: ["TAMPA", "ST. PETERSBURG", "CLEARWATER", "SARASOTA",
        "BRADENTON", "BRANDON", "WESLEY CHAPEL"],
      target_zip_prefixes: ["336", "337", "342"],
    },
    "Miami-Dade": {
      target_cities: ["MIAMI", "MIAMI BEACH", "CORAL GABLES", "HIALEAH",
        "DORAL", "HOMESTEAD", "KENDALL"],
      target_zip_prefixes: ["331", "330"],
    },
    "Jacksonville": {
      target_cities: ["JACKSONVILLE", "JACKSONVILLE BEACH", "ATLANTIC BEACH",
        "ORANGE PARK", "PONTE VEDRA"],
      target_zip_prefixes: ["322"],
    },
    "Palm Beach": {
      target_cities: ["WEST PALM BEACH", "BOCA RATON", "DELRAY BEACH",
        "JUPITER", "WELLINGTON", "PALM BEACH GARDENS"],
      target_zip_prefixes: ["334"],
    },
    "Naples · SW FL": {
      target_cities: ["NAPLES", "FORT MYERS", "CAPE CORAL", "BONITA SPRINGS",
        "ESTERO", "MARCO ISLAND"],
      target_zip_prefixes: ["339", "341"],
    },
  };

  const applyPreset = (name) => {
    const p = PRESETS[name];
    if (!p) return;
    setConfig({ ...config, target_label: name, ...p });
    toast.info(`Preset “${name}” aplicado — clique Salvar para confirmar.`);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/sunbiz/config`, config);
      setConfig(data.config);
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    if (!leads.length) return toast.message("Nada para exportar");
    const headers = [
      "document_number", "company_name", "officer_name", "email", "phone",
      "principal_city", "principal_zip", "file_date", "status",
      "enrichment_source", "agentlead_injected",
    ];
    const rows = leads.map((l) => headers.map((h) => `"${(l[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([headers.join(",") + "\n" + rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sunbiz-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const maxCount = useMemo(
    () => Math.max(1, ...series.map((s) => s.count)),
    [series],
  );

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <div className="max-w-7xl mx-auto px-6 py-8" data-testid="sunbiz-page">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B7280]">Sunbiz Connector</p>
              <h1 className="font-display text-[28px] font-black text-[#111827] tracking-tight">
                Pipeline Sunbiz → AgenteLead Pro
              </h1>
              <p className="text-[13px] text-[#6B7280] mt-1">
                Captura diária de novas LLCs na Flórida · Orlando / Orange County
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ClaireHelpButton
                testid="sunbiz"
                question="Como funciona o pipeline diário do Sunbiz, qual o filtro de cidades-alvo e como rodo manualmente?"
              />
              <button onClick={runDiagnose} data-testid="sunbiz-diagnose"
                className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#F9FAFB]"
                title="Ver o que o Apify retorna sem persistir nada (diagnóstico)">
                <Search className="w-3.5 h-3.5" /> Diagnosticar
              </button>
              <button onClick={loadAll} data-testid="sunbiz-refresh"
                className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#F9FAFB]">
                <RefreshCw className="w-3.5 h-3.5" /> Atualizar
              </button>
              <a href="/kanban?source=sunbiz" data-testid="sunbiz-open-kanban"
                title="Ver os leads do Sunbiz no CRM Kanban"
                className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#F9FAFB] text-[#7C3AED]">
                <Database className="w-3.5 h-3.5" /> Ver no CRM
              </a>
              <button onClick={runNow} disabled={running} data-testid="sunbiz-run-now"
                className="h-10 px-4 rounded-lg bg-[#84CC16] text-white text-[13px] font-bold inline-flex items-center gap-2 hover:bg-[#65A30D] disabled:opacity-50">
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Executar Agora
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-5 border-b border-[#E5E7EB]" data-testid="sunbiz-tabs">
            {[
              { id: "dashboard", label: "Dashboard", icon: Building2 },
              { id: "leads",     label: `Leads (${leadsTotal})`, icon: Database },
              { id: "config",    label: "Configurações", icon: Sparkles },
              { id: "logs",      label: `Logs (${runs.length})`, icon: Clock },
            ].map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`sunbiz-tab-${t.id}`}
                className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition-colors inline-flex items-center gap-1.5 ${
                  tab === t.id ? "border-[#84CC16] text-[#111827]" : "border-transparent text-[#6B7280] hover:text-[#111827]"
                }`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {/* ────────── DASHBOARD TAB ────────── */}
          {tab === "dashboard" && stats && (
            <div className="space-y-5" data-testid="sunbiz-dashboard">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi testid="sunbiz-kpi-captured" label="Capturados hoje" value={stats.today.captured} accent="#3B82F6" />
                <Kpi testid="sunbiz-kpi-enriched" label="Enriquecidos hoje" value={stats.today.enriched} accent="#8B5CF6" />
                <Kpi testid="sunbiz-kpi-injected" label="Injetados hoje" value={stats.today.injected} accent="#84CC16" />
                <Kpi testid="sunbiz-kpi-success" label="Taxa de sucesso" value={`${stats.success_rate_pct}%`} accent="#F59E0B" />
              </div>

              {/* Iter118 — last-run banner: shows operator that the daily
                  cron is actually firing, with date/hour, trigger and
                  how many leads it brought in.
                  Iter119 — adds "Enviar todos do dia para Outreach". */}
              {stats.last_run && (
                <div
                  data-testid="sunbiz-last-run"
                  className="al-card p-3 flex items-center justify-between gap-3 text-[12px]"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`w-2 h-2 rounded-full ${stats.last_run.ok ? "bg-emerald-500" : "bg-[#DC2626]"}`} />
                    <span className="text-[#374151] font-semibold">
                      Último disparo:
                    </span>
                    <span className="text-[#111827]">
                      {new Date(stats.last_run.started_at).toLocaleString("pt-BR")}
                    </span>
                    <span className="text-[#6B7280]">·</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#E5E7EB] text-[10px] uppercase font-semibold text-[#374151]">
                      {stats.last_run.triggered_by || "cron"}
                    </span>
                    {typeof stats.last_run.total_inserted === "number" && (
                      <span className="text-emerald-700 font-semibold">
                        +{stats.last_run.total_inserted} leads
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#6B7280] text-[11px]">
                      Próximo: {stats.next_run_utc}
                    </span>
                    <DispatchTodayButton onSent={loadAll} />
                  </div>
                </div>
              )}

              <div className="al-card p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[14px] font-bold text-[#111827]">Leads capturados · últimos 30 dias</h3>
                  <span className="text-[11px] text-[#6B7280]">Total: {stats.total} · Injetados: {stats.total_injected}</span>
                </div>
                <div className="flex items-end gap-1 h-32" data-testid="sunbiz-series">
                  {series.length === 0 ? (
                    <div className="text-[12px] text-[#9CA3AF] italic">Sem dados ainda. Clique em "Executar Agora" para começar.</div>
                  ) : series.map((s) => (
                    <div key={s.date} className="flex-1 flex flex-col items-center justify-end"
                         title={`${s.date}: ${s.count} leads`}>
                      <div className="w-full rounded-t-sm transition-all"
                        style={{
                          height: `${Math.max(8, (s.count / maxCount) * 100)}%`,
                          backgroundColor: "#84CC16",
                        }} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="al-card p-5">
                <h3 className="text-[14px] font-bold text-[#111827] mb-3">Pipeline status</h3>
                <ol className="grid grid-cols-1 md:grid-cols-5 gap-2">
                  {[
                    { id: 1, label: "Capture · Apify Sunbiz", icon: Search },
                    { id: 2, label: "Filter · Orlando/Orange", icon: MapPin },
                    { id: 3, label: "Enrich · Hunter + Apollo", icon: Sparkles },
                    { id: 4, label: "Inject · CRM AgenteLead", icon: Database },
                    { id: 5, label: "Outreach · Resend", icon: Mail },
                  ].map((s) => (
                    <li key={s.id} className="flex items-center gap-2 p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
                      <span className="w-6 h-6 rounded-full bg-[#84CC16] text-white text-[11px] font-bold inline-flex items-center justify-center">
                        {s.id}
                      </span>
                      <s.icon className="w-3.5 h-3.5 text-[#6B7280]" />
                      <span className="text-[11.5px] font-semibold text-[#374151]">{s.label}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* ────────── LEADS TAB ────────── */}
          {tab === "leads" && (
            <div className="space-y-3" data-testid="sunbiz-leads-tab">
              <div className="flex flex-wrap gap-2 items-center">
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  data-testid="sunbiz-filter-status"
                  className="h-9 px-2 rounded-md border border-[#E5E7EB] text-[12px] bg-white"
                  style={{ colorScheme: "light" }}>
                  <option value="">Todos status</option>
                  {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>

                <select
                  value={filterState}
                  onChange={(e) => { setFilterState(e.target.value); setFilters({ ...filters, city: "" }); }}
                  data-testid="sunbiz-filter-state"
                  className="h-9 px-2 rounded-md border border-[#E5E7EB] text-[12px] bg-white w-32"
                  style={{ colorScheme: "light" }}
                >
                  {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
                </select>

                <select
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  data-testid="sunbiz-filter-city"
                  className="h-9 px-2 rounded-md border border-[#E5E7EB] text-[12px] bg-white w-52"
                  style={{ colorScheme: "light" }}
                >
                  <option value="">Todas as cidades ({cityOptions.length})</option>
                  {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>

                {/* Iter118 — calendar filters: Filing date (data oficial do
                    registro no Sunbiz) e Captured date (quando nosso cron
                    capturou). Os dois cobrem casos diferentes de drill-down. */}
                <input
                  type="date"
                  value={filters.file_date || ""}
                  onChange={(e) => setFilters({ ...filters, file_date: e.target.value })}
                  data-testid="sunbiz-filter-filedate"
                  title="Data oficial do registro Sunbiz (file_date)"
                  className="h-9 px-2 rounded-md border border-[#E5E7EB] text-[12px] bg-white w-40"
                  style={{ colorScheme: "light" }}
                />
                <input
                  type="date"
                  value={filters.captured_date || ""}
                  onChange={(e) => setFilters({ ...filters, captured_date: e.target.value })}
                  data-testid="sunbiz-filter-captureddate"
                  title="Data em que o pipeline capturou esse lead"
                  className="h-9 px-2 rounded-md border border-[#E5E7EB] text-[12px] bg-white w-40"
                  style={{ colorScheme: "light" }}
                />
                {(filters.file_date || filters.captured_date) && (
                  <button
                    data-testid="sunbiz-filter-clear-dates"
                    onClick={() => setFilters({
                      ...filters, file_date: "", captured_date: "",
                    })}
                    className="h-9 px-2 rounded-md text-[11px] text-[#6B7280] hover:text-[#111827] underline"
                    title="Limpar filtros de data"
                  >
                    Limpar datas
                  </button>
                )}

                <button onClick={refreshLeadsFiltered}
                  data-testid="sunbiz-filter-apply"
                  className="h-9 px-3 rounded-md bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] font-bold">
                  Filtrar
                </button>
                <button onClick={exportCsv}
                  data-testid="sunbiz-export-csv"
                  className="h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-[12px] font-semibold hover:bg-[#F9FAFB]">
                  Exportar CSV
                </button>
              </div>

              <div className="al-card overflow-hidden">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-[#F9FAFB] text-[11px] uppercase tracking-wider text-[#6B7280]">
                    <tr>
                      <th className="px-3 py-2.5 w-8">
                        <input
                          type="checkbox"
                          data-testid="sunbiz-select-all"
                          onChange={toggleSelectAll}
                          checked={
                            leads.filter((l) => l.email).length > 0 &&
                            leads.filter((l) => l.email).every((l) => selected.has(l.document_number))
                          }
                          aria-label="Selecionar todos"
                        />
                      </th>
                      <th className="text-left px-3 py-2.5">Empresa</th>
                      <th className="text-left px-3 py-2.5">Dono</th>
                      <th className="text-left px-3 py-2.5">Email</th>
                      <th className="text-left px-3 py-2.5">Cidade</th>
                      <th className="text-left px-3 py-2.5">Data abertura</th>
                      <th className="text-left px-3 py-2.5">Status</th>
                      <th className="text-right px-3 py-2.5">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.length === 0 && (
                      <tr><td colSpan={8} className="px-3 py-6 text-center text-[#9CA3AF] italic">Nenhum lead capturado ainda.</td></tr>
                    )}
                    {leads.map((l) => (
                      <tr key={l.document_number}
                        data-testid={`sunbiz-row-${l.document_number}`}
                        onClick={() => openLeadDetail(l)}
                        className={`border-t border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer ${
                          selected.has(l.document_number) ? "bg-[#FAF5FF]" : ""
                        }`}>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            data-testid={`sunbiz-select-${l.document_number}`}
                            checked={selected.has(l.document_number)}
                            onChange={() => toggleSelect(l.document_number)}
                            disabled={!l.email}
                            title={l.email ? "Selecionar para campanha" : "Sem e-mail enriquecido"}
                          />
                        </td>
                        <td className="px-3 py-2 font-semibold text-[#111827]">{l.company_name}</td>
                        <td className="px-3 py-2 text-[#374151]">{l.officer_name || "—"}</td>
                        <td className="px-3 py-2 text-[#374151]">
                          {l.email ? <a href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()} className="text-[#3B82F6] hover:underline">{l.email}</a> : <span className="text-[#9CA3AF]">—</span>}
                        </td>
                        <td className="px-3 py-2 text-[#374151]">{l.principal_city || "—"}</td>
                        <td className="px-3 py-2 text-[#6B7280]">{l.file_date || "—"}</td>
                        <td className="px-3 py-2"><StatusChip status={l.status} /></td>
                        <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => handleSunbizEmail(l)}
                            data-testid={`sunbiz-email-${l.document_number}`}
                            disabled={!l.email}
                            className="text-[11px] font-bold text-[#7C3AED] hover:underline disabled:opacity-30 mr-3">
                            ✉ E-mail
                          </button>
                          <button onClick={() => reprocessLead(l.document_number)}
                            data-testid={`sunbiz-reprocess-${l.document_number}`}
                            className="text-[11px] font-bold text-[#3B82F6] hover:underline">
                            Reprocessar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ────────── CONFIG TAB ────────── */}
          {tab === "config" && config && (
            <div className="al-card p-5 space-y-4" data-testid="sunbiz-config-tab">
              <div className="grid md:grid-cols-2 gap-4">
                <label className="flex items-center gap-2 text-[13px] font-semibold cursor-pointer">
                  <input type="checkbox" checked={!!config.enabled}
                    onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                    data-testid="sunbiz-cfg-enabled"
                    className="w-4 h-4" />
                  Ativar pipeline cron diário
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-[#6B7280]">Cron UTC:</span>
                  <input type="number" min="0" max="23" value={config.cron_hour_utc}
                    onChange={(e) => setConfig({ ...config, cron_hour_utc: parseInt(e.target.value) || 0 })}
                    data-testid="sunbiz-cfg-hour"
                    className="h-8 w-16 px-2 rounded border border-[#E5E7EB] text-[12px] bg-white" />
                  <span className="text-[12px]">:</span>
                  <input type="number" min="0" max="59" value={config.cron_minute_utc}
                    onChange={(e) => setConfig({ ...config, cron_minute_utc: parseInt(e.target.value) || 0 })}
                    data-testid="sunbiz-cfg-min"
                    className="h-8 w-16 px-2 rounded border border-[#E5E7EB] text-[12px] bg-white" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">Dias para trás</label>
                  <input type="number" min="1" max="7" value={config.days_back}
                    onChange={(e) => setConfig({ ...config, days_back: parseInt(e.target.value) || 1 })}
                    data-testid="sunbiz-cfg-days-back"
                    className="h-9 px-2 w-full rounded border border-[#E5E7EB] text-[13px] bg-white" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">Máx. itens por run</label>
                  <input type="number" min="50" max="2000" value={config.max_items}
                    onChange={(e) => setConfig({ ...config, max_items: parseInt(e.target.value) || 500 })}
                    data-testid="sunbiz-cfg-max-items"
                    className="h-9 px-2 w-full rounded border border-[#E5E7EB] text-[13px] bg-white" />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3 pt-3 border-t border-[#E5E7EB]">
                {[
                  { key: "enable_enrich", label: "Enriquecer (Hunter + Apollo)" },
                  { key: "enable_inject", label: "Injetar no CRM" },
                  { key: "enable_outreach", label: "Outreach automático (Resend)" },
                ].map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-[12.5px] font-semibold cursor-pointer">
                    <input type="checkbox" checked={!!config[s.key]}
                      onChange={(e) => setConfig({ ...config, [s.key]: e.target.checked })}
                      data-testid={`sunbiz-cfg-${s.key}`}
                      className="w-4 h-4" />
                    {s.label}
                  </label>
                ))}
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">Resend Campaign ID (opcional)</label>
                <input type="text" value={config.resend_campaign_id || ""}
                  onChange={(e) => setConfig({ ...config, resend_campaign_id: e.target.value })}
                  data-testid="sunbiz-cfg-campaign"
                  placeholder="ex: cmp_abc123"
                  className="h-9 px-2 w-full rounded border border-[#E5E7EB] text-[13px] bg-white" />
              </div>

              <div className="border-t border-[#E5E7EB] pt-4 space-y-3">
                <h3 className="text-[13px] font-bold text-[#111827] flex items-center gap-2">
                  <span className="w-1 h-4 bg-[#7C3AED] rounded" />
                  Região-alvo (cidades + faixas de ZIP)
                </h3>
                <p className="text-[11.5px] text-[#6B7280] -mt-2">
                  Defina onde o pipeline deve filtrar. Vazio = padrão Orlando/Orange County.
                  Aceita qualquer cidade da Flórida (capturas vêm do estado inteiro).
                </p>

                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
                    Rótulo da região (exibido no dashboard)
                  </label>
                  <input type="text" value={config.target_label || ""}
                    onChange={(e) => setConfig({ ...config, target_label: e.target.value })}
                    data-testid="sunbiz-cfg-target-label"
                    placeholder="ex: Tampa Bay · Miami-Dade · Orlando / Orange County"
                    className="h-9 px-2 w-full rounded border border-[#E5E7EB] text-[13px] bg-white" />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
                    Cidades-alvo (separadas por vírgula, maiúsculas)
                  </label>
                  <input type="text"
                    value={(config.target_cities || []).join(", ")}
                    onChange={(e) => setConfig({
                      ...config,
                      target_cities: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
                    })}
                    data-testid="sunbiz-cfg-target-cities"
                    placeholder="ex: TAMPA, ST. PETERSBURG, CLEARWATER, SARASOTA"
                    className="h-9 px-2 w-full rounded border border-[#E5E7EB] text-[13px] bg-white" />
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {["Orlando / Orange County", "Tampa Bay", "Miami-Dade", "Jacksonville", "Palm Beach", "Naples · SW FL"].map((p) => (
                      <button key={p} type="button" onClick={() => applyPreset(p)}
                        data-testid={`sunbiz-preset-${p.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        className="text-[11px] px-2 py-1 rounded border border-[#E5E7EB] bg-white text-[#374151] hover:border-[#7C3AED] hover:text-[#7C3AED]">
                        Aplicar “{p}”
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
                    Prefixos de ZIP (3 dígitos, separados por vírgula)
                  </label>
                  <input type="text"
                    value={(config.target_zip_prefixes || []).join(", ")}
                    onChange={(e) => setConfig({
                      ...config,
                      target_zip_prefixes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                    })}
                    data-testid="sunbiz-cfg-target-zips"
                    placeholder="ex: 328 (Orlando) · 336,337 (Tampa) · 331 (Miami)"
                    className="h-9 px-2 w-full rounded border border-[#E5E7EB] text-[13px] bg-white" />
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button onClick={saveConfig} disabled={saving}
                  data-testid="sunbiz-cfg-save"
                  className="h-10 px-5 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-50">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar
                </button>
              </div>
            </div>
          )}

          {/* ────────── LOGS TAB ────────── */}
          {tab === "logs" && (
            <div className="al-card overflow-hidden" data-testid="sunbiz-logs-tab">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#F9FAFB] text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="text-left px-3 py-2.5">Início</th>
                    <th className="text-left px-3 py-2.5">Trigger</th>
                    <th className="text-left px-3 py-2.5">Status</th>
                    <th className="text-right px-3 py-2.5">Capturados</th>
                    <th className="text-right px-3 py-2.5">Filtrados</th>
                    <th className="text-right px-3 py-2.5">Enriquecidos</th>
                    <th className="text-right px-3 py-2.5">Injetados</th>
                    <th className="text-right px-3 py-2.5">Falhas</th>
                    <th className="text-left px-3 py-2.5">Erro</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-6 text-center text-[#9CA3AF] italic">Nenhuma execução registrada ainda.</td></tr>
                  )}
                  {runs.map((r) => (
                    <tr key={r.id} className="border-t border-[#F3F4F6]">
                      <td className="px-3 py-2 text-[#374151]">{new Date(r.started_at).toLocaleString("pt-BR")}</td>
                      <td className="px-3 py-2 text-[#6B7280]">{r.triggered_by}</td>
                      <td className="px-3 py-2">
                        {r.status === "success" ? <CheckCircle2 className="w-4 h-4 text-[#16A34A]" /> :
                         r.status === "running" ? <Loader2 className="w-4 h-4 animate-spin text-[#3B82F6]" /> :
                         <AlertCircle className="w-4 h-4 text-[#DC2626]" />}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.leads_captured}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.leads_filtered}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.leads_enriched}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-[#84CC16]">{r.leads_injected}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#DC2626]">{r.leads_failed}</td>
                      <td className="px-3 py-2 text-[11px] text-[#DC2626] truncate max-w-[200px]" title={r.error_message || ""}>
                        {r.error_message || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Iter108 — Lead detail drawer (reused from /prospecting) */}
      <LeadDetailDrawer
        open={!!drawerLead}
        lead={drawerLead}
        onClose={() => setDrawerLead(null)}
        onChanged={() => refreshLeadsFiltered()}
      />

      {/* Iter108 — Cold-email modal (reused from /prospecting) with
          a Templates picker button that pulls /api/library/templates. */}
      <EmailModal
        open={emailOpen}
        onOpenChange={setEmailOpen}
        lead={emailLead}
        email={email}
        loading={emailLoading}
        onRegenerate={(opts) => emailLead && handleSunbizEmail(emailLead, opts)}
      />

      {/* Floating "Templates" picker — visible while EmailModal is open. */}
      {emailOpen && (
        <button
          onClick={() => { setTemplatePicker(true); loadTemplates(); }}
          data-testid="sunbiz-open-templates"
          className="fixed bottom-6 right-6 z-[100] h-10 px-4 rounded-full bg-[#7C3AED] text-white text-[12px] font-bold shadow-lg hover:bg-[#6D28D9] inline-flex items-center gap-2"
        >
          <Sparkles className="w-3.5 h-3.5" /> Templates
        </button>
      )}

      {templatePicker && (
        <div className="fixed inset-0 bg-black/40 z-[110] flex items-center justify-center p-4"
             onClick={() => setTemplatePicker(false)}>
          <div onClick={(e) => e.stopPropagation()}
               data-testid="sunbiz-templates-modal"
               className="al-card w-full max-w-xl max-h-[80vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[15px]">Biblioteca de Templates · E-mail PT</h3>
              <button onClick={() => setTemplatePicker(false)} className="text-[#6B7280] hover:text-rose-500">✕</button>
            </div>
            {templates.length === 0 ? (
              <p className="text-[12.5px] text-[#9CA3AF] italic">
                Nenhum template ainda. Vá em <a href="/master/templates" className="text-[#7C3AED] underline">Master → Templates</a> para criar.
              </p>
            ) : (
              <div className="space-y-2">
                {templates.map((t) => (
                  <button key={t.id}
                    onClick={() => applyTemplate(t)}
                    data-testid={`sunbiz-tpl-${t.id}`}
                    className="block w-full text-left p-3 rounded-lg border border-[#E5E7EB] hover:border-[#7C3AED] hover:bg-[#FAF5FF] transition-colors">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-bold">{t.name}</span>
                      {t.category && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#7C3AED] font-semibold">{t.category}</span>
                      )}
                    </div>
                    <p className="text-[11.5px] text-[#6B7280] line-clamp-2 mt-1">{t.preview}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Iter109 — Sticky "Campanha 1-clique" bar (visible when ≥1 selected). */}
      {selected.size > 0 && tab === "leads" && (
        <div
          data-testid="sunbiz-campaign-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[55] flex items-center gap-3 px-4 py-2.5 rounded-full shadow-2xl ring-1 ring-white/10 bg-gradient-to-r from-[#0F1B40] to-[#1E2A6B] text-white"
        >
          <span className="text-[12px] font-bold">
            {selected.size} lead{selected.size > 1 ? "s" : ""} selecionado{selected.size > 1 ? "s" : ""}
          </span>
          <button
            onClick={() => setSelected(new Set())}
            data-testid="sunbiz-campaign-clear"
            className="text-[11.5px] font-semibold text-white/70 hover:text-white"
          >
            Limpar
          </button>
          <span className="w-px h-5 bg-white/20" />
          <button
            onClick={openCampaign}
            data-testid="sunbiz-campaign-open"
            className="h-9 px-4 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-[12.5px] font-bold inline-flex items-center gap-2"
          >
            🚀 Disparar Campanha 1-clique
          </button>
        </div>
      )}

      {/* Iter109 — Campaign confirm modal. */}
      {campaignOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[120] flex items-center justify-center p-4"
          onClick={() => !campaignRunning && setCampaignOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()}
            data-testid="sunbiz-campaign-modal"
            className="al-card w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <header className="px-5 py-3 border-b border-[var(--al-border)] flex items-center justify-between">
              <div>
                <h2 className="text-[15px] font-bold inline-flex items-center gap-2">
                  🚀 Campanha 1-clique · Sunbiz
                </h2>
                <p className="text-[11.5px] text-[#6B7280]">
                  Disparar para {selected.size} lead{selected.size > 1 ? "s" : ""} selecionado{selected.size > 1 ? "s" : ""}.
                </p>
              </div>
              <button onClick={() => !campaignRunning && setCampaignOpen(false)}
                className="text-[#6B7280] hover:text-rose-500">✕</button>
            </header>
            <div className="p-5 space-y-4 overflow-y-auto">
              {!campaignReport && (
                <>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">
                      Subject (opcional)
                    </label>
                    <select value={pickedSubject}
                      onChange={(e) => setPickedSubject(e.target.value)}
                      data-testid="sunbiz-campaign-subject"
                      className="w-full h-10 px-2 rounded-lg border border-[#E5E7EB] bg-white text-[13px]">
                      <option value="">— sem template (usa default) —</option>
                      {subjectTemplates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.category ? ` · ${t.category}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">
                      Body (obrigatório)
                    </label>
                    <select value={pickedBody}
                      onChange={(e) => setPickedBody(e.target.value)}
                      data-testid="sunbiz-campaign-body"
                      className="w-full h-10 px-2 rounded-lg border border-[#E5E7EB] bg-white text-[13px]">
                      <option value="">— escolha um template —</option>
                      {bodyTemplates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.category ? ` · ${t.category}` : ""}
                        </option>
                      ))}
                    </select>
                    {pickedBody && (
                      <div className="mt-2 p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB] text-[12px] text-[#374151] whitespace-pre-line max-h-40 overflow-y-auto"
                           data-testid="sunbiz-campaign-preview">
                        {(bodyTemplates.find((t) => t.id === pickedBody)?.content || "").slice(0, 800)}
                      </div>
                    )}
                  </div>
                  <div className="text-[11.5px] text-[#9CA3AF] bg-amber-50 border border-amber-200 rounded-lg p-3">
                    ⚠️ Variáveis <code>{"{first_name}"}</code>, <code>{"{company_name}"}</code>,
                    <code> {"{city}"}</code>, <code>{"{sector}"}</code>, <code>{"{sender_name}"}</code> serão
                    substituídas automaticamente. Leads já contatados serão pulados.
                  </div>
                </>
              )}
              {campaignReport && (
                <div data-testid="sunbiz-campaign-report" className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                      <div className="text-[22px] font-black text-emerald-700">{campaignReport.sent}</div>
                      <div className="text-[10.5px] uppercase tracking-wider text-emerald-700/80 font-semibold">Enviados</div>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <div className="text-[22px] font-black text-amber-700">{campaignReport.skipped}</div>
                      <div className="text-[10.5px] uppercase tracking-wider text-amber-700/80 font-semibold">Pulados</div>
                    </div>
                    <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
                      <div className="text-[22px] font-black text-rose-700">{campaignReport.failed}</div>
                      <div className="text-[10.5px] uppercase tracking-wider text-rose-700/80 font-semibold">Falhas</div>
                    </div>
                  </div>
                  <details>
                    <summary className="cursor-pointer text-[12px] font-semibold text-[#6B7280] hover:text-[#111827]">
                      Detalhes por lead ({campaignReport.results.length})
                    </summary>
                    <div className="mt-2 max-h-48 overflow-y-auto text-[11.5px] divide-y divide-[#F3F4F6]">
                      {campaignReport.results.map((r) => (
                        <div key={r.document_number} className="py-1.5 flex items-center justify-between gap-3">
                          <span className="font-mono text-[#374151] truncate">{r.document_number}</span>
                          <span className={`text-[10.5px] px-1.5 py-0.5 rounded font-semibold ${
                            r.status === "sent"    ? "bg-emerald-100 text-emerald-700" :
                            r.status === "skipped" ? "bg-amber-100 text-amber-700" :
                                                      "bg-rose-100 text-rose-700"
                          }`}>{r.status}{r.reason ? ` · ${r.reason}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </div>
            <footer className="px-5 py-3 border-t border-[var(--al-border)] flex justify-end gap-2 bg-[#F9FAFB]">
              {campaignReport ? (
                <button onClick={() => { setCampaignOpen(false); setCampaignReport(null); }}
                  data-testid="sunbiz-campaign-close-report"
                  className="h-9 px-4 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12.5px] font-bold">
                  Fechar
                </button>
              ) : (
                <>
                  <button onClick={() => setCampaignOpen(false)} disabled={campaignRunning}
                    data-testid="sunbiz-campaign-cancel"
                    className="h-9 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold hover:bg-[#F3F4F6] disabled:opacity-50">
                    Cancelar
                  </button>
                  <button onClick={runCampaign}
                    disabled={!pickedBody || campaignRunning}
                    data-testid="sunbiz-campaign-fire"
                    className="h-9 px-4 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-[12.5px] font-bold inline-flex items-center gap-2 disabled:opacity-50">
                    {campaignRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "🚀"}
                    Disparar agora ({selected.size})
                  </button>
                </>
              )}
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Iter119 — DispatchTodayButton.
 * Two-step UX: 1) GET /dispatch-today/preview returns eligible count,
 *              2) POST /dispatch-today fires the campaign.
 */
function DispatchTodayButton({ onSent }) {
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPreview = async () => {
    try {
      const { data } = await axios.get(`${API}/sunbiz/dispatch-today/preview`);
      setPreview(data); setConfirming(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar preview");
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/sunbiz/dispatch-today`, {});
      toast.success(`${data.sent || 0} mensagens enviadas`);
      setConfirming(false); setPreview(null);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no envio");
    } finally { setSending(false); }
  };

  return (
    <>
      <button
        type="button"
        data-testid="sunbiz-dispatch-today"
        onClick={loadPreview}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11.5px] font-semibold"
      >
        Enviar todos do dia
      </button>
      {confirming && preview && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setConfirming(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white p-6 space-y-4 text-[#111827]"
            data-testid="sunbiz-dispatch-modal"
          >
            <div className="font-bold text-[16px]">
              Enviar todos do dia para Outreach
            </div>
            <div className="text-[12.5px] text-[#374151] space-y-1">
              <div>📅 Janela: últimas 24h</div>
              <div>📋 Capturados: <strong>{preview.captured}</strong></div>
              <div>✅ Elegíveis (com e-mail e não enviados): <strong>{preview.eligible}</strong></div>
              <div>⏭️ Serão pulados: <strong>{preview.skipped_no_email_or_already_sent}</strong></div>
            </div>
            {preview.sample?.length > 0 && (
              <div className="rounded-md bg-[#F3F4F6] p-2 text-[11.5px] text-[#374151] space-y-1">
                <div className="font-semibold uppercase text-[10px] tracking-wide text-[#6B7280]">
                  Amostra
                </div>
                {preview.sample.map((s) => (
                  <div key={s.document_number} className="truncate">
                    · {s.company_name} <span className="text-[#6B7280]">({s.email})</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirming(false)}
                className="h-9 px-3 rounded-lg bg-[#E5E7EB] hover:bg-[#D1D5DB] text-[#374151] text-[12px] font-semibold"
                data-testid="sunbiz-dispatch-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={send}
                disabled={sending || preview.eligible === 0}
                data-testid="sunbiz-dispatch-confirm"
                className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold disabled:opacity-50"
              >
                {sending ? "Enviando..." : `Disparar para ${preview.eligible} leads`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
